package org.multiplatform.kotfolio.pages.kmm

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.core.Page
@Page("/kmm/log")
@Composable
fun KMPLogPage() {
    BlogLayout(
        PageProperties(
            "log",
            ""
        ),
        BlogProperties("Kotlin Multiplatform", "Log")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            KotlinCode(
                code = """
                    import co.touchlab.kermit.Logger as KermitLogger

                    interface LogInterface {
                        fun v(message: String)
                        fun v(throwable: Throwable)
                        fun d(message: String)
                        fun d(throwable: Throwable)
                        fun i(message: String)
                        fun i(throwable: Throwable)
                        fun w(message: String)
                        fun w(throwable: Throwable)
                        fun e(message: String)
                        fun e(throwable: Throwable)
                    }
                    
                    object Log : LogInterface {
                        override fun v(message: String) {
                            KermitLogger.v(message)
                        }

                        override fun v(throwable: Throwable) {
                            KermitLogger.v(throwable) {
                                throwable.message ?: throwable.stackTraceToString()
                            }
                        }

                        override fun d(message: String) {
                            KermitLogger.d(message)
                        }

                        override fun d(throwable: Throwable) {
                            KermitLogger.d(throwable) {
                                throwable.message ?: throwable.stackTraceToString()
                            }
                        }

                        override fun i(message: String) {
                            KermitLogger.i(message)
                        }

                        override fun i(throwable: Throwable) {
                            KermitLogger.i(throwable) {
                                throwable.message ?: throwable.stackTraceToString()
                            }
                        }

                        override fun w(message: String) {
                            KermitLogger.w(message)
                        }

                        override fun w(throwable: Throwable) {
                            // TODO: report to crashlytics
                            KermitLogger.w(throwable) {
                                throwable.message ?: throwable.stackTraceToString()
                            }
                        }

                        override fun e(message: String) {
                            KermitLogger.e(message)
                        }

                        override fun e(throwable: Throwable) {
                            // TODO: report to crashlytics
                            KermitLogger.e(throwable) {
                                throwable.message ?: throwable.stackTraceToString()
                            }
                        }
                    }
                    
                    //Use
                    Log.v("")
                    """.trimIndent()
            )

            //https://github.com/mheerwaarden/CMPEventDemo.git
            KotlinCode(
                code = """
                    kermit = "2.0.6"
                    touchlab-kermit = { module = "co.touchlab:kermit", version.ref = "kermit" }
                    touchlab-kermit-simple = { module = "co.touchlab:kermit-simple", version.ref = "kermit" }
                    
                    val commonMain by getting {
                        dependencies {
                            api(libs.touchlab.kermit)
                        }
                    }
                    
                    import co.touchlab.kermit.Logger
                    import co.touchlab.kermit.StaticConfig
                    import co.touchlab.kermit.platformLogWriter

                    fun initLogger(): Logger {
                        // platformLogWriter() is a relatively simple config option, useful for local debugging. For production
                        // uses you *may* want to have a more robust configuration from the native platform. In KaMP Kit,
                        // that would likely go into platformModule expect/actual.
                        // See https://github.com/touchlab/Kermit
                        return Logger(config = StaticConfig(logWriterList = listOf(platformLogWriter())), "ComposeApp")
                    }
                    
                    private val coreModule = module {
                        factory { (tag: String?) -> get<Logger>().withTag(tag ?: "Event") }
                        
                        single<Logger> { initLogger() }
                    }
                    
                    // Simple function to clean up the syntax a bit
                    fun KoinComponent.injectLogger(tag: String): Lazy<Logger> = inject { parametersOf(tag) }
                    
                    fun initKoin(platformModule: Module): KoinApplication {
                        println("Starting Koin...")
                        val koinApplication = startKoin {
                            modules(
                                // Common core modules
                                // Platform independent, in Kotlin
                                coreModule
                            )
                        }

                        // Dummy initialization logic, making use of appModule declarations for demonstration purposes.
                        val koin = koinApplication.koin
                        // doOnStartup is a lambda which is implemented in Swift on iOS side
                        val doOnStartup = koin.get<() -> Unit>()
                        doOnStartup.invoke()

                        println("Koin initialized")
                        return koinApplication
                    }
                    
                    //KTOR
                    install(Logging) {
                        logger = Logger.DEFAULT
                        level = LogLevel.ALL // Max logging for debugging Ktor
                    }
                    
                    //iosMain
                    import co.touchlab.kermit.Logger
                    import kotlinx.coroutines.CoroutineExceptionHandler

                    fun kermitExceptionHandler(log: Logger) = CoroutineExceptionHandler { _, throwable ->
                        throwable.printStackTrace()
                        log.e(throwable = throwable) { "Error in MainScope" }
                    }
                    
                    // Access from Swift to create a logger
                    @Suppress("unused")
                    fun Koin.loggerWithTag(tag: String) = get<Logger>(qualifier = null) { parametersOf(tag) }
                    
                    @Suppress("unused")
                    fun initKoinIos(
                        doOnStartup: () -> Unit
                    ): KoinApplication = initKoin(
                        module {
                            single { doOnStartup }
                        }
                    )
                    
                    //swift
                    func startKoin() {
                        let doOnStartup = { NSLog("Startup iOS/Swift") }

                        let koinApplication = KoinIOSKt.doInitKoinIos(
                            doOnStartup: doOnStartup
                        )
                        _koin = koinApplication.koin
                    }
                    
                    //androidMain
                    class MainApp : Application() {
                        override fun onCreate() {
                            super.onCreate()
                            initKoin(
                                module {
                                    single {
                                        { Log.i("Startup", "Startup Android/Kotlin") }
                                    }
                                }
                            )
                        }
                    }
                    
                    //Access Logger
                    private val logger: Logger //constructor
                    logger.e(throwable = e) { "Error saving isReadOnly preference" }
                    """.trimIndent()
            )
        }
    }
}

//private val logger = Logger.withTag(this@ChangeViewModel::class.simpleName.toString())
//logger.e(e) { "Error changing password" }